import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnDestroy,
	ChangeDetectionStrategy
} from '@angular/core';
import { Subject } from 'rxjs';
import { LibraryTreeDataSource } from './library-tree-datasource';
import { TranslatePipe } from '@ngx-translate/core';

import {
	LibraryCollection,
	LibraryCollectionItemsAndFolders
} from '@go-modules/interfaces/library/library-collection';
import { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import {
	CONTENT_TYPES,
	MODES,
	SORT_OPTIONS
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import {
	CollectionFolderViewerModule
} from '@go-modules/components/library/collection-folder-viewer/collection-folder-viewer.module';
import {
	CollectionItemViewerModule
} from '@go-modules/components/library/collection-item-viewer/collection-item-viewer.module';
import { LibraryTreeNode } from '@go-modules/components/library/library-tree-viewer/library-tree-node';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';

@Component({
	selector: 'ngx-library-tree-viewer',
	templateUrl: './library-tree-viewer.component.html',
	styleUrls: ['./library-tree-viewer.component.scss'],
	imports: [
		MatTreeModule,
		MatMenuModule,
		MatIconModule,
		MatCheckboxModule,
		FormsModule,
		MatProgressBarModule,
		TranslatePipe,
		MatButtonModule,
		NgxMediaThumbnailModule,
		MatProgressSpinnerModule,
		NgxZeroStateModule,
		CollectionFolderViewerModule,
		CollectionItemViewerModule
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibraryTreeViewerComponent implements OnDestroy {
	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public mode: string;
	@Input() public multiSelect: boolean = false;
	@Input() public sortBy: string = SORT_OPTIONS.LASTMODIFIED;
	@Input() public searchStr: string = '';
	@Input() public orgSettings: OrgSettings;
	@Input() public expandedHeight: number = 370;
	@Input() public currentlySelected: { id?: number; hash?: string; folderId?: number; };

	@Output() public selectItem = new EventEmitter<any>();
	@Output() public setCurrentlySelected = new EventEmitter<any>();
	@Output() public loadNewItems = new EventEmitter<any>();
	@Output() public setActivityPanelIsOpen = new EventEmitter<boolean>();
	@Output() public setSelectAllChecked = new EventEmitter<boolean>();

	public MODES = MODES;
	public CONTENT_TYPES = CONTENT_TYPES;
	private destroy$ = new Subject<void>();

	public constructor (
		public dataSource: LibraryTreeDataSource
	) {}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.dataSource.disconnect();
	}

	public handleExpandedChange (node: LibraryTreeNode, expanded: boolean): void {
		if (expanded && !this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
		} else if (!expanded && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
		}
	}

	public isFolderEmpty (node: LibraryTreeNode): boolean {
		if (node.type !== 'folder' || node.isLoading) {
			return false;
		}

		if (!this.dataSource.isExpanded(node)) {
			return false;
		}

		const nodeIndex = this.dataSource.data.indexOf(node);
		if (nodeIndex === -1 || nodeIndex === this.dataSource.data.length - 1) {
			return true; // Last node or not found
		}

		// Check if next node is a child (has higher level)
		const nextNode = this.dataSource.data[nodeIndex + 1];
		return !nextNode || nextNode.level <= node.level;
	}

	public trackNode = (_index: number, node: LibraryTreeNode): string => {
		return node.key;
	};

	public handleSelectItem (event: any): void {
		this.selectItem.emit(event);
	}
}
